import React from "react";
import { Phone, MapPin, Clock, Mail, Navigation } from "lucide-react";
import { restaurantInfo } from "../data/mock";
import { useReveal } from "../hooks/useReveal";
import { useLanguage } from "../context/LanguageContext";

export default function Contact({ onReserve }) {
  const { t } = useLanguage();
  const r = useReveal();
  const mapQuery = encodeURIComponent(restaurantInfo.address);

  return (
    <section id="contact" className="py-24 bg-ivory relative">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={r.ref} className={`reveal ${r.visible ? "in" : ""} text-center mb-14`}>
          <p className="section-sub">{t.contact.sub}</p>
          <h2 className="section-title text-4xl md:text-5xl mt-3">{t.contact.title}</h2>
          <div className="ornament mt-4">
            <MapPin className="text-[#D4A94A]" size={20} />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-5">
            <ContactCard icon={MapPin} title={t.contact.address}>
              <p className="text-[#1B1512]/85">{restaurantInfo.address}</p>
              <a
                target="_blank"
                rel="noreferrer"
                href={`https://www.google.com/maps/dir/?api=1&destination=${mapQuery}`}
                className="inline-flex items-center gap-2 mt-3 text-[#E86D1F] font-semibold hover:text-[#7A1F1F] transition-colors"
              >
                <Navigation size={16} /> {t.contact.route}
              </a>
            </ContactCard>

            <ContactCard icon={Phone} title={t.contact.phone}>
              <a href={`tel:${restaurantInfo.phoneClean}`} className="text-[#1B1512]/85 hover:text-[#E86D1F] transition-colors font-semibold text-lg">
                {restaurantInfo.phone}
              </a>
              <p className="text-sm text-[#1B1512]/60 mt-1">{t.contact.phoneNote}</p>
            </ContactCard>

            <ContactCard icon={Mail} title={t.contact.email}>
              <a href={`mailto:${restaurantInfo.email}`} className="text-[#1B1512]/85 hover:text-[#E86D1F] transition-colors">
                {restaurantInfo.email}
              </a>
            </ContactCard>

            <ContactCard icon={Clock} title={t.contact.hours}>
              {t.contact.days.map((h, i) => (
                <div key={i} className="flex justify-between text-sm md:text-base py-1 border-b border-[#D4A94A]/20 last:border-0">
                  <span className="font-semibold text-[#7A1F1F]">{h.day}</span>
                  <span className="text-[#1B1512]/75">{h.time}</span>
                </div>
              ))}
            </ContactCard>

            <div className="flex flex-wrap gap-3 pt-2">
              <a href={`tel:${restaurantInfo.phoneClean}`} className="btn-primary"><Phone size={16} /> {t.contact.callBtn}</a>
              <button onClick={onReserve} className="btn-outline">{t.contact.reserveBtn}</button>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden shadow-2xl border-4 border-[#D4A94A]/30 min-h-[500px] relative">
            <iframe
              title="PURI PURI Standort"
              src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
              className="w-full h-full absolute inset-0"
              loading="lazy"
              style={{ border: 0, minHeight: 500 }}
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-[#D4A94A]/20 hover:border-[#E86D1F]/40 transition-all hover:-translate-y-1 duration-300">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-[#7A1F1F] flex items-center justify-center shrink-0 border-2 border-[#D4A94A]">
          <Icon className="text-[#D4A94A]" size={20} />
        </div>
        <div className="flex-1">
          <h4 className="font-display text-[#7A1F1F] text-lg font-bold tracking-wide uppercase">{title}</h4>
          <div className="mt-2">{children}</div>
        </div>
      </div>
    </div>
  );
}
